import { useCallback } from 'react';

type JSONValue = string | number | boolean | null | JSONValue[] | { [key: string]: JSONValue };

function getISOTimestamp(): string {
  // Example: 2026-01-15T14-32-05
  return new Date().toISOString().replace(/:/g, '-').split('.')[0];
}

export function useDownloadJSON() {
  return {
    downloadJSON: useCallback(<T extends JSONValue>(data: T, baseFilename = 'data'): void => {
      const json = JSON.stringify(data, null, 2);
      const filename = `${baseFilename}-${getISOTimestamp()}.json`;

      const blob = new Blob([json], {
        type: 'application/json',
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = filename;
      link.click();

      URL.revokeObjectURL(url);
    }, []),
    downloadJSONInNewTab: useCallback(<T extends JSONValue>(data: T, baseFilename = 'data'): void => {
      const json = JSON.stringify(data, null, 2);
      const filename = `${baseFilename}-${getISOTimestamp()}.json`;

      // For CEF and Citrix, open JSON in new window for user to save
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.title = filename;

        const pre = newWindow.document.createElement('pre');
        pre.style.overflowWrap = 'break-word';
        pre.style.whiteSpace = 'pre-wrap';
        pre.textContent = json;

        newWindow.document.body.appendChild(pre);
      } else {
        // Fallback: copy to clipboard if window.open is blocked
        if (navigator.clipboard?.writeText) {
          navigator.clipboard
            .writeText(json)
            .then(() => {
              alert('Download is not supported in this environment. JSON data has been copied to your clipboard.');
            })
            .catch(() => {
              alert('Download is not supported in this environment, and clipboard copy also failed.');
            });
        } else {
          alert('Download is not supported in this environment, and clipboard access is not available.');
        }
      }
    }, []),
  };
}
